/**
 * Copyright 2025 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 */

import { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import VERTC, { MediaType } from '@volcengine/rtc';
import { Modal } from '@arco-design/web-react';
import Utils from '@/utils/utilsTTS/utils';
import RtcClient from '@/libTTS/RtcClient';
import {
  clearCurrentMsg,
  clearHistoryMsg,
  localJoinRoom,
  localLeaveRoom,
  updateAIGCState,
  updateLocalUser,
} from '@/store/storeTTS/slices/room';

import useRtcListeners from '@/libTTS/listenerHooks';
import { RootState } from '@/store/storeTTS';

import {
  updateMediaInputs,
  updateSelectedDevice,
  setDevicePermissions,
} from '@/store/storeTTS/slices/device';
import logger from '@/utils/utilsTTS/logger';
import aigcConfig, { ScreenShareScene, isVisionMode } from '@/configTTS';

export interface FormProps {
  username: string;
  roomId: string;
  publishAudio: boolean;
}

export const useVisionMode = () => {
  const room = useSelector((state: RootState) => state.room);
  return {
    isVisionMode: isVisionMode(room.aiConfig?.Config?.LLMConfig.ModelName),
    isScreenMode: ScreenShareScene.includes(room.scene),
  };
};

export const useDeviceState = () => {
  const dispatch = useDispatch();
  const room = useSelector((state: RootState) => state.room);
  const localUser = room.localUser;
  const isAudioPublished = localUser.publishAudio ?? false;
  const isVideoPublished = localUser.publishVideo ?? false;
  const isScreenPublished = localUser.publishScreen ?? false;
  const device = useSelector((state: RootState) => state.device);
  const devicePermissions = device.devicePermissions;
  // 添加状态跟踪，防止并发调用
  const switchCameraLock = useRef(false);
  const switchMicLock = useRef(false);
  const queryDevices = async (type: MediaType) => {
    const mediaDevices = await RtcClient.getDevices({
      audio: type === MediaType.AUDIO,
      video: type === MediaType.VIDEO,
    });
    if (type === MediaType.AUDIO) {
      dispatch(
        updateMediaInputs({
          audioInputs: mediaDevices.audioInputs,
        })
      );
      dispatch(
        updateSelectedDevice({
          selectedMicrophone: mediaDevices.audioInputs[0]?.deviceId,
        })
      );
    } else {
      dispatch(
        updateMediaInputs({
          videoInputs: mediaDevices.videoInputs,
        })
      );
      dispatch(
        updateSelectedDevice({
          selectedCamera: 'user'
        })
      );
    }
    return mediaDevices;
  };
  // const queryDevices = async (type: MediaType) => {
  //   const mediaDevices = await RtcClient.getDevices({
  //     audio: type === MediaType.AUDIO,
  //     video: type === MediaType.VIDEO,
  //   });
  //   if (type === MediaType.AUDIO) {
  //     dispatch(
  //       updateMediaInputs({
  //         audioInputs: mediaDevices.audioInputs,
  //       })
  //     );
  //     dispatch(
  //       updateSelectedDevice({
  //         selectedMicrophone: mediaDevices.audioInputs[0]?.deviceId,
  //       })
  //     );
  //   } else {
  //     dispatch(
  //       updateMediaInputs({
  //         videoInputs: mediaDevices.videoInputs,
  //       })
  //     );

  //     // 默认选择第一个摄像头设备
  //     let selectedCameraDeviceId = mediaDevices.videoInputs[0]?.deviceId;

  //     // 只在移动端优先选择后置摄像头
  //     if (Utils.isMobile() && mediaDevices.videoInputs.length > 0) {
  //       // 查找后置摄像头（标签包含back、rear、后或后置关键词）
  //       const backCamera = mediaDevices.videoInputs.find(device =>
  //         device.label.toLowerCase().includes('back') ||
  //         device.label.toLowerCase().includes('rear') ||
  //         device.label.toLowerCase().includes('后') ||
  //         device.label.toLowerCase().includes('后置')
  //       );

  //       if (backCamera) {
  //         selectedCameraDeviceId = backCamera.deviceId;
  //       } else {
  //         // 如果没有明确标记为后置的摄像头，则选择第一个非前置摄像头
  //         const nonFrontCamera = mediaDevices.videoInputs.find(device =>
  //           !device.label.toLowerCase().includes('front') &&
  //           !device.label.toLowerCase().includes('前置')
  //         );

  //         if (nonFrontCamera) {
  //           selectedCameraDeviceId = nonFrontCamera.deviceId;
  //         }
  //       }
  //     }

  //     dispatch(
  //       updateSelectedDevice({
  //         selectedCamera: selectedCameraDeviceId,
  //       })
  //     );
  //   }
  //   return mediaDevices;
  // };

  const switchMic = async (controlPublish = true) => {
    // 防止并发调用
    if (switchMicLock.current) {
      console.warn('Microphone switch is in progress, please wait');
      return Promise.reject(new Error('Microphone switch is in progress'));
    }

    switchMicLock.current = true;
    try {
      if (controlPublish) {
        await (!isAudioPublished
          ? RtcClient.publishStream(MediaType.AUDIO)
          : RtcClient.unpublishStream(MediaType.AUDIO));
      }
      queryDevices(MediaType.AUDIO);
      try {
        if (!isAudioPublished) {
          // 先尝试停止音频捕获，以解决权限状态不一致的问题
          try {
            await RtcClient.stopAudioCapture();
          } catch (e) {
            // 忽略停止操作的错误
            console.log('stopAudioCapture error (ignoring):', e);
          }
          // 然后重新开始音频捕获
          await RtcClient.startAudioCapture();
        } else {
          await RtcClient.stopAudioCapture();
        }
      } catch (error) {
        logger.error('Microphone permission error:', error);
        // 更新设备权限状态
        dispatch(setDevicePermissions({ audio: false, video: devicePermissions?.video ?? false }));
        if (!isAudioPublished) {
          dispatch(
            updateLocalUser({
              publishAudio: false,
            })
          );
        }
        throw error;
      }
      dispatch(
        updateLocalUser({
          publishAudio: !isAudioPublished,
        })
      );
    } finally {
      switchMicLock.current = false;
    }
  };



  const switchCamera = async (controlPublish = true) => {
    // 防止并发调用
    if (switchCameraLock.current) {
      console.warn('Camera switch is in progress, please wait');
      return Promise.reject(new Error('Camera switch is in progress'));
    }

    switchCameraLock.current = true;
    try {
      const LocalVideoID = 'local-video-player';

      if (controlPublish) {
        await (!isVideoPublished
          ? RtcClient.publishStream(MediaType.VIDEO)
          : RtcClient.unpublishStream(MediaType.VIDEO));
      }

      queryDevices(MediaType.VIDEO);
      try {
        if (!isVideoPublished) {
          // 开启摄像头 - 先确保停止任何现有的捕获，然后重新开始
          try {
            await RtcClient.stopVideoCapture();
          } catch (e) {
            console.log('stopVideoCapture error (ignoring):', e);
          }
          await new Promise(resolve => setTimeout(resolve, 100));
          // 检查是否已经开启视频捕获，避免重复开启
          try {
            await RtcClient.startVideoCapture();
          } catch (error: any) {
            // 如果错误是"Has already capture"，则忽略该错误
            if (error?.message?.includes('Has already capture')) {
              console.warn('视频捕获已经开启，跳过重复启动');
            } else {
              // 如果是其他错误，则重新抛出
              throw error;
            }
          }
        } else {
          // 关闭摄像头
          await RtcClient.stopVideoCapture();
          // 清除视频播放器状态
          try {
            RtcClient.setLocalVideoPlayer(
              localUser.username!,
              undefined,
              false
            );
          } catch (e) {
            console.log('clear video player error (ignoring):', e);
          }
        }
      } catch (error) {
        console.error('Error in switchCamera:', error);
        throw error;
      }

      dispatch(
        updateLocalUser({
          publishVideo: !isVideoPublished,
        })
      );

      // 修复：确保在开启摄像头时重新设置视频播放器
      if (!isVideoPublished) {
        setTimeout(() => {
          const localUser = room.localUser;
          const useScreenPlayer = localUser.publishScreen;
          try {
            RtcClient.setLocalVideoPlayer(
              localUser.username!,
              LocalVideoID,
              useScreenPlayer
            );
          } catch (error) {
            console.error('Error setting local video player:', error);
          }
        }, 100);
      }
    } finally {
      switchCameraLock.current = false;
    }
  };



  const switchScreenCapture = async (controlPublish = true) => {
    try {
      if (controlPublish) {
        await (!isScreenPublished
          ? RtcClient.publishScreenStream(MediaType.VIDEO)
          : RtcClient.unpublishScreenStream(MediaType.VIDEO));
      }
      await (!isScreenPublished ? RtcClient.startScreenCapture() : RtcClient.stopScreenCapture());
      dispatch(
        updateLocalUser({
          publishScreen: !isScreenPublished,
        })
      );
    } catch {
      console.warn('Not Authorized.');
    }
  };

  return {
    isAudioPublished,
    isVideoPublished,
    isScreenPublished,
    switchMic,
    switchCamera,
    switchScreenCapture,
  };
};

export const useGetDevicePermission = () => {
  const [permission, setPermission] = useState<{
    audio: boolean;
  }>();

  const dispatch = useDispatch();

  useEffect(() => {
    (async () => {
      const permission = await RtcClient.checkPermission();
      dispatch(setDevicePermissions(permission));
      setPermission(permission);
    })();
  }, [dispatch]);
  return permission;
};

export const useJoin = (): [
  boolean,
  (formValues: FormProps, fromRefresh: boolean) => Promise<void | boolean>
] => {
  const devicePermissions = useSelector((state: RootState) => state.device.devicePermissions);
  const room = useSelector((state: RootState) => state.room);

  const dispatch = useDispatch();

  const { switchCamera, switchMic } = useDeviceState();
  const [joining, setJoining] = useState(false);
  const listeners = useRtcListeners();

  const handleAIGCModeStart = async () => {
    await RtcClient.stopAudioBot();
    if (room.isAIGCEnable) {
      dispatch(clearCurrentMsg());
    }
    await RtcClient.startAudioBot();

    dispatch(updateAIGCState({ isAIGCEnable: true }));
  };

  async function disPatchJoin(formValues: FormProps): Promise<boolean | undefined> {
    if (joining) {
      return;
    }

    const isSupported = await VERTC.isSupported();
    if (!isSupported) {
      Modal.error({
        title: '不支持 RTC',
        content: '您的浏览器可能不支持 RTC 功能，请尝试更换浏览器或升级浏览器后再重试。',
      });
      return;
    }

    setJoining(true);
    const { username, roomId } = formValues;
    const isVision = isVisionMode(aigcConfig.Model);
    const shouldGetVideoPermission = isVision && !ScreenShareScene.includes(room.scene);

    const token = aigcConfig.BaseConfig.Token;

    /** 1. Create RTC Engine */
    const engineParams = {
      appId: aigcConfig.BaseConfig.AppId,
      roomId,
      uid: username,
    };
    await RtcClient.createEngine(engineParams);

    /** 2.1 Set events callbacks */
    RtcClient.addEventListeners(listeners);
    // 预先设置本地用户的音频状态，避免中间态
    if (devicePermissions.audio) {
      dispatch(
        updateLocalUser({
          publishAudio: true,
        })
      );
    }
    /** 2.2 RTC starting to join room */
    await RtcClient.joinRoom(token!, username);
    console.log(' ------ userJoinRoom\n', `roomId: ${roomId}\n`, `uid: ${username}`);
    /** 3. Set users' devices info */
    const mediaDevices = await RtcClient.getDevices({
      audio: true,
      video: shouldGetVideoPermission,
    });

    dispatch(
      localJoinRoom({
        roomId,
        user: {
          username,
          userId: username,
        },
      })
    );
    dispatch(
      updateSelectedDevice({
        selectedMicrophone: mediaDevices.audioInputs[0]?.deviceId,
        selectedCamera: mediaDevices.videoInputs[0]?.deviceId,
      })
    );
    dispatch(updateMediaInputs(mediaDevices));

    setJoining(false);

    if (devicePermissions.audio) {
      try {
        // 确保每次加入房间时默认开启麦克风
        await RtcClient.startAudioCapture();
        await RtcClient.publishStream(MediaType.AUDIO);
        dispatch(
          updateLocalUser({
            publishAudio: true,
          })
        );
        // RtcClient.setAudioVolume(30);
      } catch (e) {
        logger.debug('No permission for mic');
      }
    }

    // 只有在明确需要开启摄像头时才开启，而不是默认开启
    if (devicePermissions.video && shouldGetVideoPermission &&
      (room.localUser.publishVideo === undefined || room.localUser.publishVideo === false)) {
      try {
        await switchCamera();
      } catch (e) {
        logger.debug('No permission for camera');
      }
    }

    Utils.setSessionInfo({
      username,
      roomId,
      publishAudio: true,
    });
    handleAIGCModeStart();
  }

  return [joining, disPatchJoin];
};

export const useLeave = () => {
  const dispatch = useDispatch();

  return async function () {
    await Promise.all([
      RtcClient.stopAudioCapture,
      RtcClient.stopScreenCapture,
      RtcClient.stopVideoCapture,
    ]);
    await RtcClient.leaveRoom();
    dispatch(clearHistoryMsg());
    dispatch(clearCurrentMsg());
    dispatch(localLeaveRoom());
    dispatch(updateAIGCState({ isAIGCEnable: false }));
  };
};
